import React, { useState } from 'react';
import {
  X,
  Search,
  Globe,
  Image as ImageIcon,
  Sliders,
  RotateCcw,
  ExternalLink,
  HelpCircle,
  ArrowRight,
  Eye,
  EyeOff
} from 'lucide-react';
import { useLiveEditorScope } from '../../context/LiveEditorScopeContext';
import { useStorefrontData } from '../../context/StorefrontDataContext';
import InsertImageModal from '../blog/InsertImageModal';

export interface SectionDescriptor {
  id: string;
  label: string;
  key?: string;
  description?: string;
}

interface LivePageDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pageTitle?: string;
  sections?: SectionDescriptor[];
}

export const DEFAULT_HOMEPAGE_SECTIONS: SectionDescriptor[] = [
  { id: 'hero-section', label: '1. Hero Banner Video/Foto', key: 'hero' },
  { id: 'style-preference-section', label: '2. Pilihan Momen (Gaya Editorial)', key: 'stylePreference' },
  { id: 'showcase-section', label: '3. Koleksi Pilihan (Showcase)', key: 'showcase' },
  { id: 'ugc-section', label: '4. Video Komunitas (UGC)', key: 'ugc' },
  { id: 'faq-section', label: '5. Tanya Jawab (FAQ)', key: 'faq' },
  { id: 'cta-section', label: '6. Banner Penawaran (CTA)', key: 'cta' }
];

export default function LivePageDrawer({
  isOpen,
  onClose,
  pageTitle: propPageTitle,
  sections: propSections
}: LivePageDrawerProps) {
  const scope = useLiveEditorScope();
  const storefront = useStorefrontData();

  const pageTitle = propPageTitle || scope.pageTitle || 'Halaman Storefront';
  const sections = propSections || scope.sectionsList || DEFAULT_HOMEPAGE_SECTIONS;

  const cms = scope.data || scope.cms;
  const updateCmsField = scope.updateField || scope.updateCmsField;
  const discardDraftChanges = scope.discardDraftChanges;
  const hasUnpublishedChanges = scope.hasUnpublishedChanges;
  const lastSavedTime = scope.lastSavedTime;
  const isSectionHidden = scope.isSectionHidden;
  const toggleSectionVisibility = scope.toggleSectionVisibility;

  const [activeTab, setActiveTab] = useState<'seo' | 'sections' | 'danger'>('sections');
  const [showOgImageModal, setShowOgImageModal] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [activeJumpSectionId, setActiveJumpSectionId] = useState<string | null>(null);

  if (!isOpen) return null;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      // Do NOT call onClose() - keep drawer open as requested
      const lenis = (window as any).lenis;
      if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(el, { offset: -30, duration: 1 });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      setActiveJumpSectionId(id);

      // Temporary visual pulse/ring on target section in document
      el.classList.add('ring-4', 'ring-[#722F38]/40', 'ring-offset-4', 'transition-all', 'duration-500');
      setTimeout(() => {
        el.classList.remove('ring-4', 'ring-[#722F38]/40', 'ring-offset-4');
        setActiveJumpSectionId((prev) => (prev === id ? null : prev));
      }, 2500);
    } else {
      console.warn(`[LivePageDrawer] Section element #${id} tidak ditemukan di halaman.`);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm('Apakah Anda yakin ingin membatalkan semua perubahan draft dan mengembalikan ke versi publik saat ini?')) {
      return;
    }
    setIsDiscarding(true);
    if (discardDraftChanges) {
      await discardDraftChanges();
    }
    setIsDiscarding(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-black/25 backdrop-blur-[2px] transition-opacity animate-fade-in overflow-hidden h-[100dvh]"
      data-lenis-prevent="true"
    >
      {/* Backdrop overlay */}
      <div className="flex-1 cursor-pointer" onClick={onClose} />

      {/* Drawer content */}
      <div
        className="w-full max-w-md bg-[#FCFCFB] text-ink h-full h-[100dvh] max-h-[100dvh] shadow-2xl flex flex-col border-l border-border-subtle z-10 overflow-hidden"
        data-lenis-prevent="true"
      >
        {/* Header */}
        <div className="px-6 py-4.5 border-b border-border-subtle flex items-center justify-between bg-white shrink-0">
          <div>
            <span className="text-[9px] font-mono font-bold tracking-widest uppercase text-brand-wine">
              Pengaturan Halaman
            </span>
            <h2 className="text-lg font-serif font-bold text-ink">{pageTitle}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-muted hover:text-ink hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            title="Tutup Panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab switch */}
        <div className="flex border-b border-border-subtle bg-gray-50/80 px-6 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('sections')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'sections'
                ? 'border-brand-wine text-brand-wine bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            Bagian & Visibilitas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('seo')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'seo'
                ? 'border-brand-wine text-brand-wine bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            SEO & Social
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('danger')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'danger'
                ? 'border-red-600 text-red-600 bg-white'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            Riwayat & Reset
          </button>
        </div>

        {/* Tab content area */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 space-y-6 touch-pan-y"
          data-lenis-prevent="true"
        >
          {/* TAB 1: SECTIONS & VISIBILITY */}
          {activeTab === 'sections' && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-[11px] text-amber-900 leading-relaxed">
                💡 <strong>Kontrol Visibilitas:</strong> Klik ikon mata (👁️) untuk menyembunyikan atau menampilkan seksi di mata pengunjung publik. Seksi yang disembunyikan akan diberi tanda transparan saat Anda mengedit.
              </div>

              <div className="space-y-2">
                {sections.map((sec) => {
                  const hidden = isSectionHidden ? isSectionHidden(sec.id) : false;
                  const isCurrentTarget = activeJumpSectionId === sec.id;
                  return (
                    <div
                      key={sec.id}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                        isCurrentTarget
                          ? 'border-brand-wine bg-brand-wine/5 shadow-xs ring-1 ring-brand-wine/30'
                          : hidden
                          ? 'border-amber-200/80 bg-amber-50/30'
                          : 'border-border-subtle/80 bg-white hover:border-brand-wine/50 hover:shadow-xs'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => scrollToSection(sec.id)}
                        className="flex-1 text-left flex items-center gap-2.5 cursor-pointer group pr-2"
                        title="Lompat ke seksi ini di halaman (tanpa menutup panel)"
                      >
                        <span
                          className={`text-xs font-semibold group-hover:text-brand-wine transition-colors ${
                            isCurrentTarget
                              ? 'text-brand-wine font-bold'
                              : hidden
                              ? 'text-muted line-through decoration-amber-600/50'
                              : 'text-ink'
                          }`}
                        >
                          {sec.label}
                        </span>
                        <ArrowRight
                          className={`w-3.5 h-3.5 transition-all ${
                            isCurrentTarget
                              ? 'text-brand-wine translate-x-1 opacity-100'
                              : 'text-muted opacity-40 group-hover:opacity-100 group-hover:text-brand-wine group-hover:translate-x-0.5'
                          }`}
                        />
                      </button>

                      {/* Visibility Toggle Button */}
                      <button
                        type="button"
                        onClick={() => toggleSectionVisibility && toggleSectionVisibility(sec.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold tracking-wider uppercase transition-all cursor-pointer ${
                          hidden
                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/60'
                        }`}
                        title={hidden ? 'Klik untuk tampilkan ke publik' : 'Klik untuk sembunyikan dari publik'}
                      >
                        {hidden ? (
                          <>
                            <EyeOff className="w-3.5 h-3.5" />
                            <span>Hidden</span>
                          </>
                        ) : (
                          <>
                            <Eye className="w-3.5 h-3.5" />
                            <span>Tampil</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Master Sitewide Components Shortcut */}
              <div className="pt-4 border-t border-border-subtle/80 space-y-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-wider text-amber-700">
                  <Globe className="w-3.5 h-3.5" />
                  <span>Komponen Master Sitewide</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      storefront.openMasterDrawer('footer');
                    }}
                    className="p-2.5 rounded-xl border border-amber-200/80 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 text-left transition-all flex flex-col gap-1 cursor-pointer"
                  >
                    <span className="text-xs font-bold text-gray-900 flex items-center justify-between">
                      <span>Master Footer</span>
                      <ExternalLink className="w-3 h-3 text-amber-600" />
                    </span>
                    <span className="text-[10px] text-muted">Kolom link & sosmed</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      storefront.openMasterDrawer('cta');
                    }}
                    className="p-2.5 rounded-xl border border-amber-200/80 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 text-left transition-all flex flex-col gap-1 cursor-pointer"
                  >
                    <span className="text-xs font-bold text-gray-900 flex items-center justify-between">
                      <span>Master CTA</span>
                      <ExternalLink className="w-3 h-3 text-amber-600" />
                    </span>
                    <span className="text-[10px] text-muted">Banner penawaran</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: SEO & SOCIAL SHARE */}
          {activeTab === 'seo' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-ink mb-1.5 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-brand-wine" />
                  <span>Meta Title (Judul Google)</span>
                </label>
                <input
                  type="text"
                  value={cms?.seo?.title || ''}
                  onChange={(e) => updateCmsField && updateCmsField('seo', 'title', e.target.value)}
                  placeholder="Secera | Modest Modern Scarf & Apparel"
                  className="w-full px-3 py-2 text-xs border border-border-subtle rounded-lg focus:outline-none focus:border-brand-wine bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-ink mb-1.5 flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5 text-brand-wine" />
                  <span>Meta Description (Deskripsi Cuplikan Google)</span>
                </label>
                <textarea
                  rows={3}
                  value={cms?.seo?.description || ''}
                  onChange={(e) => updateCmsField && updateCmsField('seo', 'description', e.target.value)}
                  placeholder="Koleksi busana modest modern berpadu dengan keanggunan abadi..."
                  className="w-full px-3 py-2 text-xs border border-border-subtle rounded-lg focus:outline-none focus:border-brand-wine bg-white resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-ink mb-1.5 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-brand-wine" />
                  <span>Social Share Image (OG Image saat tautan dibagikan)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cms?.seo?.ogImage || ''}
                    onChange={(e) => updateCmsField && updateCmsField('seo', 'ogImage', e.target.value)}
                    placeholder="https://secera.id/og-image.jpg"
                    className="flex-1 px-3 py-2 text-xs border border-border-subtle rounded-lg focus:outline-none focus:border-brand-wine bg-white font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOgImageModal(true)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-ink text-xs font-bold rounded-lg transition-colors cursor-pointer shrink-0"
                  >
                    Pilih
                  </button>
                </div>
                {cms?.seo?.ogImage && (
                  <div className="mt-2 relative aspect-[1200/630] rounded-lg overflow-hidden border border-border-subtle max-w-xs shadow-xs">
                    <img src={cms.seo.ogImage} alt="OG Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: DANGER & RESET */}
          {activeTab === 'danger' && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200/80 text-amber-900 space-y-2">
                <div className="flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-amber-700" />
                  <h4 className="text-xs font-bold uppercase tracking-wider">Status Versi Halaman</h4>
                </div>
                <p className="text-xs leading-relaxed">
                  {hasUnpublishedChanges
                    ? '⚠️ Saat ini Anda memiliki perubahan draft yang belum dipublikasikan ke pengunjung umum.'
                    : '✅ Semua perubahan draft sudah sinkron dengan versi publik yang dilihat pengunjung.'}
                </p>
                {lastSavedTime && (
                  <p className="text-[11px] text-amber-700/80 font-mono">
                    Terakhir disimpan otomatis: {lastSavedTime} WIB
                  </p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  disabled={isDiscarding}
                  onClick={handleDiscard}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 font-bold text-xs rounded-xl transition-all cursor-pointer disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Batalkan Draft & Revert ke Versi Publik</span>
                </button>
                <p className="text-[11px] text-muted text-center mt-2 leading-relaxed">
                  Tindakan ini akan menghapus draf kerja Anda dan mengembalikan tampilan halaman ke kondisi yang saat ini tampil di publik.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal for Social OG Image */}
        {showOgImageModal && (
          <InsertImageModal
            isOpen={showOgImageModal}
            onClose={() => setShowOgImageModal(false)}
            onInsert={(url) => {
              if (updateCmsField) updateCmsField('seo', 'ogImage', url);
              setShowOgImageModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
